import { useMemo } from 'react';

import type { ApprovalActor } from 'ui-product-approvals-common';
import type { Actor } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc';
import { useAppHubUsers } from './useAppHubUsers';
import { useBreezeAgents } from './useBreezeAgents';
import { useAppTeams } from './useAppTeams';
import { actorToApprovalUser } from '../utils/approvalResponseAdapters';
import { ACTOR } from '../constants/actors';

export interface UseResolvedActors {
  approvers: ApprovalActor[];
  isLoading: boolean;
}

export const useResolvedActors = (
  actors: ReadonlyArray<Actor>
): UseResolvedActors => {
  const userIds = useMemo(
    () =>
      actors
        .filter((actor) => actor.actorType === ACTOR.USER)
        .map((actor) => Number(actor.id)),
    [actors]
  );

  const agentIds = useMemo(
    () =>
      actors
        .filter((actor) => actor.actorType === ACTOR.AGENT)
        .map((actor) => actor.id),
    [actors]
  );

  const hasTeamApprovers = useMemo(
    () => actors.some((actor) => actor.actorType === ACTOR.TEAM),
    [actors]
  );

  const { usersRecord, loading: usersLoading } = useAppHubUsers({ userIds });

  const { agentsRecord, loading: agentsLoading } = useBreezeAgents({
    agentIds,
  });

  const { teamsRecord, loading: teamsLoading } = useAppTeams({
    enabled: hasTeamApprovers,
  });

  const approvers = useMemo<ApprovalActor[]>(
    () =>
      actors.map((actor) =>
        actorToApprovalUser(actor, usersRecord, agentsRecord, teamsRecord)
      ),
    [actors, usersRecord, agentsRecord, teamsRecord]
  );

  return {
    approvers,
    isLoading: usersLoading || agentsLoading || teamsLoading,
  };
};
